import { Injectable, computed, effect, inject } from '@angular/core';

import { RoleKind } from '../models/brijjsports.enums';
import { BrijjSportsDb } from '../models/brijjsports.models';
import { BrijjSportsStateService } from '../state/brijjsports-state.service';
import { TenantContextService } from '../tenant/tenant-context.service';
import { Tenant } from '../tenant/tenant.model';
import { MOCK_PASSWORD, MockUser, userRoles } from './mock-user.model';

/** Deterministic local-part generator (lowercased, dotted, no special chars). */
function emailLocalPart(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, '')
    .trim()
    .replace(/\s+/g, '.');
}

function ownerEmail(tenant: Tenant): string {
  return `owner@${tenant.slug}.brijjsports.demo`;
}

function userEmail(tenant: Tenant, name: string, suffix: string): string {
  return `${emailLocalPart(name)}.${suffix}@${tenant.slug}.brijjsports.demo`;
}

/**
 * Mock authentication service. Treats every Owner / Coach / Parent / Athlete
 * across every org tenant as a user account derived from seeded data.
 *
 * Catalog is built lazily and cached. Calling `signIn` switches the active
 * tenant, sets the role + acting-as context, and marks the user signed in.
 */
@Injectable({ providedIn: 'root' })
export class MockAuthService {
  private readonly state = inject(BrijjSportsStateService);
  private readonly tenants = inject(TenantContextService);

  private _catalog: MockUser[] | null = null;

  constructor() {
    // Whenever the active tenant's roster changes (CRUD on coaches/parents/athletes),
    // drop the cached catalog so the login picker reflects current data on next read.
    effect(() => {
      // Track collection identities so any add/update/delete triggers re-build.
      this.state.coaches();
      this.state.parents();
      this.state.athletes();
      this._catalog = null;
    });
  }

  readonly currentUser = computed<MockUser | null>(() => {
    const id = this.state.currentUserId();
    if (!id) return null;
    return this.users().find(u => u.id === id) ?? null;
  });

  readonly isSignedIn = computed(() => this.state.currentUserId() !== null);

  /** Full directory of mock users across every org tenant. */
  users(): MockUser[] {
    if (this._catalog) return this._catalog;
    this._catalog = this.buildCatalog();
    return this._catalog;
  }

  /**
   * Find a user matching the given email + password and sign them in.
   * Returns the matched user on success, or null on failure.
   */
  signIn(email: string, password: string): MockUser | null {
    const norm = email.trim().toLowerCase();
    const user = this.users().find(u => u.email.toLowerCase() === norm && u.password === password);
    if (!user) return null;

    if (this.tenants.currentTenantId() !== user.tenantId) {
      // Persist role/context/currentUser into the target tenant's storage *before*
      // switching, so the state-service tenant-swap effect rehydrates with our writes
      // already in place (it overwrites the in-memory signal with the stored snapshot).
      const sportKeys: string[] = [];
      const tenant = this.tenants.orgTenants().find(t => t.id === user.tenantId);
      if (tenant?.features.archery) sportKeys.push('archery');
      if (tenant?.features.basketball) sportKeys.push('basketball');
      if (tenant?.features.baseball) sportKeys.push('baseball');
      if (tenant?.features.softball) sportKeys.push('softball');
      const snapshot = this.state.peekOrSeedTenantSnapshot(
        user.tenantId,
        sportKeys,
        tenant?.name ?? '',
        tenant?.city ?? '',
        tenant?.ownerName,
        tenant?.slug
      );
      const next: BrijjSportsDb = {
        ...snapshot,
        currentUserId: user.id,
        currentRole: user.role,
        currentContext: {
          ...snapshot.currentContext,
          coachId: user.role === RoleKind.Coach ? (user.refId ?? null) : null,
          parentId: user.role === RoleKind.Parent ? (user.refId ?? null) : null,
          athleteId: user.role === RoleKind.Athlete ? (user.refId ?? null) : null
        }
      };
      this.state.persistTenantSnapshot(user.tenantId, next);
      this.tenants.setCurrentTenantBySlug(user.tenantSlug);
      return user;
    }

    if (user.role === RoleKind.Owner) {
      this.state.setRole(RoleKind.Owner);
    } else if (user.refId) {
      this.state.setRoleContext(user.role, user.refId);
    }
    this.state.setCurrentUser(user.id);
    return user;
  }

  signOut(): void {
    this.state.signOut();
  }

  /** Force a rebuild of the user catalog (e.g. after tenant CRUD or reset). */
  invalidate(): void {
    this._catalog = null;
  }

  /**
   * The roles the signed-in user can adopt without re-auth. Used by the
   * topbar role switcher; an empty list means there's nothing to switch to.
   */
  availableRoles(): readonly RoleKind[] {
    const user = this.currentUser();
    return user ? userRoles(user) : [];
  }

  /**
   * Switch the active role for the signed-in user. No-op when the requested
   * role isn't in the user's accessible set. Returns true on a successful
   * switch.
   *
   * Caveat: `MockUser.roles` lists the roles a user can adopt but pairs them
   * with a single shared `refId`. That's correct for Owner⇄Coach combinations
   * (Owner has no refId), but a user who is Coach AND Parent of an athlete
   * needs a per-role refId. Tracked as a follow-up — refactor to
   * `roles: { role, refId }[]` and route this method through it.
   */
  switchActiveRole(role: RoleKind): boolean {
    const user = this.currentUser();
    if (!user) return false;
    if (!userRoles(user).includes(role)) return false;
    if (role === RoleKind.Owner) {
      this.state.setRole(RoleKind.Owner);
    } else if (user.refId) {
      this.state.setRoleContext(role, user.refId);
    }
    return true;
  }

  private buildCatalog(): MockUser[] {
    const all: MockUser[] = [];
    for (const tenant of this.tenants.orgTenants()) {
      const sportKeys: string[] = [];
      if (tenant.features.archery) sportKeys.push('archery');
      if (tenant.features.basketball) sportKeys.push('basketball');
      if (tenant.features.baseball) sportKeys.push('baseball');
      if (tenant.features.softball) sportKeys.push('softball');
      const db = this.state.peekOrSeedTenantSnapshot(
        tenant.id,
        sportKeys,
        tenant.name,
        tenant.city,
        tenant.ownerName,
        tenant.slug
      );

      // Owner — one per tenant.
      all.push({
        id: `${tenant.id}::owner`,
        email: ownerEmail(tenant),
        password: MOCK_PASSWORD,
        name: db.academy?.ownerName ?? 'Owner',
        role: RoleKind.Owner,
        tenantId: tenant.id,
        tenantSlug: tenant.slug,
        tenantName: tenant.name,
        refId: null
      });

      for (const c of db.coaches) {
        all.push({
          id: `${tenant.id}::coach::${c.id}`,
          email: userEmail(tenant, c.name, 'coach'),
          password: MOCK_PASSWORD,
          name: c.name,
          role: RoleKind.Coach,
          tenantId: tenant.id,
          tenantSlug: tenant.slug,
          tenantName: tenant.name,
          refId: c.id
        });
      }

      for (const p of db.parents) {
        all.push({
          id: `${tenant.id}::parent::${p.id}`,
          email: userEmail(tenant, p.name, 'parent'),
          password: MOCK_PASSWORD,
          name: p.name,
          role: RoleKind.Parent,
          tenantId: tenant.id,
          tenantSlug: tenant.slug,
          tenantName: tenant.name,
          refId: p.id
        });
      }

      for (const a of db.athletes) {
        if (a.archived) continue;
        all.push({
          id: `${tenant.id}::athlete::${a.id}`,
          email: userEmail(tenant, a.name, 'athlete'),
          password: MOCK_PASSWORD,
          name: a.name,
          role: RoleKind.Athlete,
          tenantId: tenant.id,
          tenantSlug: tenant.slug,
          tenantName: tenant.name,
          refId: a.id
        });
      }
    }
    return all;
  }
}
